import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CloudUpload,
  Download,
  Description,
  Search,
  Person,
  List as ListIcon,
  Storage,
  PersonSearch,
  Assignment,
  CheckCircle,
  InsertDriveFile,
  FilterList,
  Clear
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useActivityLog } from '../context/ActivityLogContext';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const CustomerStyles = () => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog,
    completeActivityProcess,
  } = useActivityLog();

  const [selectedTab, setSelectedTab] = useState(0); // 0 = Customer Search, 1 = Items Check
  const [customerId, setCustomerId] = useState('');
  const [file, setFile] = useState(null);
  const [itemsList, setItemsList] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setError('Please select an Excel or CSV file only.');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleCustomerSearch = async () => {
    if (!customerId.trim()) {
      setError('Please enter a Customer ID.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    // Generate a unique process ID
    const processId = `customer-search-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(processId, `Customer Search: ${customerId.trim()}`, 'Customer Styles');
    addLog(processId, `Starting search for customer: ${customerId.trim()}`);

    try {
      const formData = new FormData();
      formData.append('customerId', customerId.trim());
      const token = localStorage.getItem('jwtToken');

      addLog(processId, 'Loading master inventory from SAF1909...');
      updateActivityProcess(processId, { progressPercentage: 20 });

      const response = await axios.post(`${API_BASE_URL}/api/CustomerStyles/search-customer`, formData, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        responseType: 'blob'
      });

      addLog(processId, 'Filtering customer assignments...');
      updateActivityProcess(processId, { progressPercentage: 60 });

      addLog(processId, 'Applying exclusion rules...');
      updateActivityProcess(processId, { progressPercentage: 80 });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 15).replace(/[-:]/g, '').replace('T', '');
      link.setAttribute('download', `${customerId}_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addLog(processId, `Successfully generated styles for customer ${customerId.trim()}`);
      updateActivityProcess(processId, { progressPercentage: 100 });
      completeActivityProcess(processId, true);

      setSuccess(`Available styles for customer ${customerId} successfully generated and downloaded!`);

    } catch (err) {
      console.error('Search error:', err);
      addLog(processId, `Error: ${err.response?.data?.message || err.message}`);
      completeActivityProcess(processId, false);
      setError(err.response?.data?.message || 'An error occurred during customer search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearchWithDetails = async () => {
    if (!customerId.trim()) {
      setError('Please enter a Customer ID.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const processId = `customer-search-details-${Date.now()}`;

    addActivityProcess(processId, `Customer Search with Details: ${customerId.trim()}`, 'Customer Styles');
    addLog(processId, `Starting search with item details for customer: ${customerId.trim()}`);

    try {
      const formData = new FormData();
      formData.append('customerId', customerId.trim());
      const token = localStorage.getItem('jwtToken');

      addLog(processId, 'Loading master inventory from SAF1909...');
      updateActivityProcess(processId, { progressPercentage: 10 });

      addLog(processId, 'Filtering customer assignments and exclusions...');
      updateActivityProcess(processId, { progressPercentage: 30 });

      addLog(processId, 'Querying Item Price Unit Detail report from AWS...');
      updateActivityProcess(processId, { progressPercentage: 50 });

      const response = await axios.post(`${API_BASE_URL}/api/CustomerStyles/search-customer-details`, formData, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        responseType: 'blob'
      });

      addLog(processId, 'Generating Excel with item details...');
      updateActivityProcess(processId, { progressPercentage: 80 });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 15).replace(/[-:]/g, '').replace('T', '');
      link.setAttribute('download', `${customerId}_Details_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addLog(processId, `Successfully generated styles with item details for customer ${customerId.trim()}`);
      updateActivityProcess(processId, { progressPercentage: 100 });
      completeActivityProcess(processId, true);

      setSuccess(`Available styles with item details for customer ${customerId} successfully generated and downloaded!`);

    } catch (err) {
      console.error('Search with details error:', err);
      addLog(processId, `Error: ${err.response?.data?.message || err.message}`);
      completeActivityProcess(processId, false);
      setError(err.response?.data?.message || 'An error occurred during customer search with details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleItemsCheck = async () => {
    if (!file && !itemsList.trim()) {
      setError('Please provide either a file or items list.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    // Generate a unique process ID
    const processId = `items-check-${Date.now()}`;
    const inputType = file ? 'file' : 'manual list';

    // Add to global activity log
    addActivityProcess(processId, `CSL Items Check (${inputType})`, 'Customer Styles');
    addLog(processId, `Starting CSL data check from ${inputType}...`);

    try {
      const formData = new FormData();
      const token = localStorage.getItem('jwtToken');

      // Determine which endpoint to call based on input
      let endpoint;
      if (file) {
        formData.append('file', file);
        endpoint = `${API_BASE_URL}/api/CustomerStyles/check-items-file`;
        addLog(processId, `Processing file: ${file.name}`);
      } else {
        formData.append('itemsList', itemsList.trim());
        endpoint = `${API_BASE_URL}/api/CustomerStyles/check-items-list`;
        const itemCount = itemsList.trim().split(/[\s,\n]+/).filter(i => i).length;
        addLog(processId, `Processing ${itemCount} items from manual entry`);
      }

      updateActivityProcess(processId, { progressPercentage: 20 });
      addLog(processId, 'Querying CSL database...');

      const response = await axios.post(endpoint, formData, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        responseType: 'blob'
      });

      updateActivityProcess(processId, { progressPercentage: 70 });
      addLog(processId, 'Generating Excel report...');

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 15).replace(/[-:]/g, '').replace('T', '');
      link.setAttribute('download', `CSL_Data_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addLog(processId, 'CSL data check completed successfully');
      updateActivityProcess(processId, { progressPercentage: 100 });
      completeActivityProcess(processId, true);

      setSuccess('CSL data check completed successfully and downloaded!');

    } catch (err) {
      console.error('Check error:', err);
      addLog(processId, `Error: ${err.response?.data?.message || err.message}`);
      completeActivityProcess(processId, false);
      setError(err.response?.data?.message || 'An error occurred during items check. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetCustomerForm = () => {
    setCustomerId('');
    setError('');
    setSuccess('');
  };

  const resetItemsForm = () => {
    setFile(null);
    setItemsList('');
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCustomerKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCustomerSearch();
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      if (droppedFile.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
        setFile(droppedFile);
        setError('');
        setSuccess('');
      } else {
        setError('Please select an Excel or CSV file only.');
      }
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Customer Styles" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          CUSTOMER STYLES
        </Typography>

        <Grid container spacing={3}>
          {/* Main Content Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              {/* Tabs */}
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '& .MuiTabs-indicator': {
                    backgroundColor: TOOL_COLORS.OTHER.primary,
                  },
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '1rem',
                  },
                }}
              >
                <Tab
                  icon={<Person />}
                  label="Customer Search"
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: TOOL_COLORS.OTHER.primary,
                    },
                  }}
                />
                <Tab
                  icon={<ListIcon />}
                  label="Items Check"
                  iconPosition="start"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: TOOL_COLORS.OTHER.primary,
                    },
                  }}
                />
              </Tabs>

              {/* Customer Search Tab */}
              {selectedTab === 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                    Search Available Styles
                  </Typography>

                  {/* Search Input */}
                  <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <PersonSearch sx={{ color: TOOL_COLORS.OTHER.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Customer ID
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        onKeyPress={handleCustomerKeyPress}
                        placeholder="Enter customer ID (e.g., CUST001)"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          onClick={handleCustomerSearch}
                          disabled={!customerId.trim() || loading}
                          startIcon={<Search />}
                          sx={{
                            bgcolor: TOOL_COLORS.OTHER.primary,
                            '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                            px: 4
                          }}
                        >
                          {loading ? 'Searching...' : 'Search Customer'}
                        </Button>

                        <Button
                          variant="contained"
                          onClick={handleCustomerSearchWithDetails}
                          disabled={!customerId.trim() || loading}
                          startIcon={<Storage />}
                          sx={{
                            bgcolor: '#1976d2',
                            '&:hover': { bgcolor: '#1565c0' },
                            px: 4
                          }}
                        >
                          {loading ? 'Searching...' : 'Search with Item Details'}
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={resetCustomerForm}
                          sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary }}
                        >
                          Reset
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Items Check Tab */}
              {selectedTab === 1 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                    Check CSL Data
                  </Typography>

                  {/* File Upload Card */}
                  <Card sx={{ mb: 3, bgcolor: file ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: TOOL_COLORS.OTHER.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Upload File (Excel/CSV)
                        </Typography>
                        {file && (
                          <Tooltip title="1 file selected">
                            <CheckCircle sx={{ color: '#4CAF50' }} />
                          </Tooltip>
                        )}
                      </Box>

                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                      />

                      {/* Drag and Drop Zone */}
                      <Box
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current.click()}
                        sx={{
                          border: `2px dashed ${isDragging ? TOOL_COLORS.OTHER.primary : file ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDragging
                            ? TOOL_COLORS.OTHER.light
                            : file
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          '&:hover': {
                            borderColor: file ? '#4CAF50' : TOOL_COLORS.OTHER.primary,
                            bgcolor: file
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : TOOL_COLORS.OTHER.light,
                          },
                        }}
                      >
                        {file ? (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              1 file selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click or drop to replace file
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.OTHER.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              Drop your file here or click to browse
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Excel (.xlsx, .xls) or CSV files only
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* File List */}
                      {file && (
                        <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 1,
                              mb: 1,
                              bgcolor: 'background.paper',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                              <Description sx={{ color: TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                                ({(file.size / 1024).toFixed(1)} KB)
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Manual Entry Card */}
                  <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Assignment sx={{ color: TOOL_COLORS.OTHER.primary }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Manual Item Entry
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={itemsList}
                        onChange={(e) => setItemsList(e.target.value)}
                        placeholder="Enter item IDs separated by commas, spaces, or new lines:&#10;&#10;ITEM001&#10;ITEM002, ITEM003&#10;ITEM004 ITEM005"
                        variant="outlined"
                      />
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleItemsCheck}
                      disabled={(!file && !itemsList.trim()) || loading}
                      startIcon={<Search />}
                      sx={{
                        bgcolor: TOOL_COLORS.OTHER.primary,
                        '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover },
                        px: 4
                      }}
                    >
                      {loading ? 'Checking...' : 'Check Items'}
                    </Button>

                    {(file || itemsList.trim()) && (
                      <Button
                        variant="outlined"
                        onClick={resetItemsForm}
                        disabled={loading}
                        startIcon={<Clear />}
                        sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Information Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    {selectedTab === 0 ? 'Searching for available styles...' : 'Checking items...'}
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.OTHER.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Feature Information */}
              {selectedTab === 0 ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    How Customer Search Works:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Storage sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Load Master Inventory"
                        secondary="Retrieves data from SAF1909"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FilterList sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Filter Assignments"
                        secondary="Excludes items already assigned to customer"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Apply Rules"
                        secondary="Filters exclusive items and discontinued products"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Download sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Generate Export"
                        secondary="Creates Excel file with available styles"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Instructions:
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    1. Enter the Customer ID<br />
                    2. Click "Search Customer"<br />
                    3. Wait for processing to complete<br />
                    4. Excel file will auto-download<br /><br />
                    <strong>Note:</strong> Discontinued items are only included if ATS quantity ≥ 3
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Items Check Options:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <InsertDriveFile sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="File Upload"
                        secondary="Upload Excel/CSV with item IDs"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Manual Entry"
                        secondary="Enter item IDs directly"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="CSL Lookup"
                        secondary="Retrieves Customer Style List data"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Download sx={{ color: TOOL_COLORS.OTHER.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Export Results"
                        secondary="Downloads Excel with CSL data"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Instructions:
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    1. Upload file OR enter items manually<br />
                    2. Click "Check Items"<br />
                    3. Wait for processing<br />
                    4. Review and download results<br /><br />
                    <strong>Accepted Formats:</strong> Excel (.xlsx, .xls) and CSV (.csv)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CustomerStyles;